import { ConfigProvider, Form, Select, TableProps, Tag } from 'antd'
import vi_VN from 'antd/es/locale/vi_VN'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { apiGetOutletActivationHistory } from '../../api'
import defaultParams from '../../assets/constants/default_params'
import DateRangePresets from '../../components/outlet/DateRangePresets'
import FormSearch from '../../components/form'
import CustomTable from '../../components/table'
import { useLoading } from '../../context/LoadingContext'
import { ITransactionConnectionView, TC_TYPE_LABELS } from '../../models/outlet.modal'
import { toastError } from '../../utils/toast'

const formatNumber = (v?: number | null) => (v == null ? '—' : v.toLocaleString('vi-VN'))
const formatCurrency = (v?: number | null) => (v == null ? '—' : `${v.toLocaleString('vi-VN')}₫`)

const fmtEpoch = (epoch?: number) => {
    if (!epoch || epoch <= 0) return '—'
    const d = dayjs.unix(epoch)
    if (!d.isValid() || d.year() < 2000) return '—'
    return d.format('DD/MM/YYYY HH:mm')
}

const fmtTs = (ts?: string) => {
    if (!ts) return '—'
    const d = dayjs(ts)
    if (!d.isValid() || d.year() < 2000) return '—'
    return d.format('DD/MM/YYYY HH:mm')
}

const TYPE_OPTIONS = [
    { value: 'CONNECTION', label: TC_TYPE_LABELS.CONNECTION },
    { value: 'EXTENSION', label: TC_TYPE_LABELS.EXTENSION },
]

type SearchState = {
    fromDate?: string
    toDate?: string
    types?: string[]
}

export default function TabActivationHistory({ outletId }: { outletId: string }) {
    const [data, setData] = useState<ITransactionConnectionView[]>([])
    const [total, setTotal] = useState<number>(0)
    const [totalActivations, setTotalActivations] = useState<number>(0)
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [search, setSearch] = useState<SearchState>({})
    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()

    const fetchData = useCallback(async () => {
        if (!outletId) return
        try {
            setIsLoading(true)
            const resp: any = await apiGetOutletActivationHistory({
                outletId,
                fromDate: search.fromDate,
                toDate: search.toDate,
                types: search.types,
                limit: pageSize,
                offset: pageSize * (currentPage - 1),
            })
            if (resp?.data == null) {
                setData([])
                setTotal(0)
                setTotalActivations(0)
                return
            }
            setData(resp.data.data ?? [])
            setTotal(Number(resp.data.total ?? 0))
            setTotalActivations(Number(resp.data.totalActivations ?? 0))
        } catch (error) {
            console.log(error)
            toastError('Không tải được lịch sử đấu nối')
        } finally {
            setIsLoading(false)
        }
    }, [outletId, search, currentPage, pageSize, setIsLoading])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const handleSearch = (values: any) => {
        const range = values.range as [dayjs.Dayjs, dayjs.Dayjs] | undefined
        setCurrentPage(defaultParams.PAGE)
        setSearch({
            fromDate: range?.[0]?.format('YYYY-MM-DD'),
            toDate: range?.[1]?.format('YYYY-MM-DD'),
            types: values.types,
        })
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setCurrentPage(defaultParams.PAGE)
        setSearch({})
    }

    const columns: TableProps<ITransactionConnectionView>['columns'] = [
        {
            key: 'STT',
            title: 'STT',
            width: 60,
            render: (_, __, index) => ++index + (pageSize * (currentPage - 1)),
        },
        {
            key: 'startDate',
            title: 'Ngày bắt đầu',
            render: (_, r) => fmtEpoch(r.startDate),
        },
        {
            key: 'createdAt',
            title: 'Ngày tạo',
            render: (_, r) => fmtTs(r.createdAt),
        },
        {
            key: 'serialSim',
            title: 'Serial',
            dataIndex: 'serialSim',
            render: v => v || '—',
        },
        {
            key: 'phoneSim',
            title: 'Số thuê bao',
            dataIndex: 'phoneSim',
            render: v => v || '—',
        },
        {
            key: 'phoneCustomer',
            title: 'SĐT khách hàng',
            dataIndex: 'phoneCustomer',
            render: v => v || '—',
        },
        {
            key: 'dataPack',
            title: 'Gói cước',
            dataIndex: 'dataPack',
            render: v => v || '—',
        },
        {
            key: 'numberDay',
            title: 'Số ngày',
            align: 'right',
            dataIndex: 'numberDay',
            render: v => formatNumber(v),
        },
        {
            key: 'type',
            title: 'Loại',
            dataIndex: 'type',
            render: v => {
                const label = TC_TYPE_LABELS[v] ?? v ?? '—'
                const color = v === 'CONNECTION' ? 'green' : v === 'EXTENSION' ? 'blue' : 'default'
                return <Tag color={color}>{label}</Tag>
            },
        },
        {
            key: 'price',
            title: 'Doanh thu',
            align: 'right',
            dataIndex: 'price',
            render: v => formatCurrency(v),
        },
    ]

    return (
        <ConfigProvider locale={vi_VN}>
            <FormSearch form={formSearch} handleSearch={handleSearch} handleClearSearch={handleClearSearch}>
                <Form.Item name='range' label='Khoảng ngày' style={{ minWidth: 300 }}>
                    <DateRangePresets />
                </Form.Item>
                <Form.Item name='types' label='Loại' style={{ minWidth: 200 }}>
                    <Select mode='multiple' allowClear placeholder='Tất cả' options={TYPE_OPTIONS} />
                </Form.Item>
            </FormSearch>

            <div style={{ margin: '8px 0 16px', color: '#666' }}>
                Hiển thị <b>{formatNumber(total)}</b> đấu nối · Tổng <b>{formatNumber(totalActivations)}</b> SIM đấu duy nhất
            </div>

            <CustomTable
                namePage='đấu nối'
                rowKey='id'
                columns={columns}
                dataSource={data}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
        </ConfigProvider>
    )
}
